<template>
  <b-form class="grc-form" @submit.prevent="submitForm">
    <b-alert
      v-if="localForm.secondLevelApprover && approvalLabel"
      show
      variant="info"
      class="grc-form__alert"
    >
      Aprovação: {{ approvalLabel }}
    </b-alert>

    <b-alert
      v-if="submitValidationError"
      show
      variant="warning"
      class="grc-form__alert py-2"
    >
      {{ submitValidationError }}
    </b-alert>

    <section class="grc-form__section">
      <div class="grc-form__section-header">
        <h3 class="grc-form__section-title">Identificação e desenho</h3>
        <p class="grc-form__section-description">
          Registre o controle, seu objetivo e a configuração principal de
          desenho.
        </p>
      </div>

      <b-row>
        <b-col cols="12" md="8">
          <b-form-group label="Nome do controle *">
            <b-form-input v-model.trim="localForm.name" required></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Status *">
            <b-form-select
              v-model="localForm.status"
              :options="statusOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Objetivo do controle *">
        <b-form-textarea
          v-model.trim="localForm.objective"
          rows="3"
          required
        ></b-form-textarea>
      </b-form-group>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Tipo *">
            <b-form-select
              v-model="localForm.controlType"
              :options="typeOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Natureza *">
            <b-form-select
              v-model="localForm.nature"
              :options="natureOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Criticidade *">
            <b-form-select
              v-model="localForm.criticality"
              :options="criticalityOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
    </section>

    <section class="grc-form__section">
      <div class="grc-form__section-header">
        <h3 class="grc-form__section-title">Execução e testes</h3>
        <p class="grc-form__section-description">
          Configure a frequência, os testes aplicados e o contexto operacional.
        </p>
      </div>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Frequência de execução *">
            <b-form-select
              v-model="localForm.frequency"
              :options="frequencyOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Teste de desenho">
            <b-form-select
              v-model="localForm.designTest"
              :options="designTestOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Teste operacional">
            <b-form-select
              v-model="localForm.operationalTest"
              :options="operationalTestOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Núcleo/Categoria interna">
            <b-form-input
              v-model.trim="localForm.internalCategory"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Área responsável *">
            <b-form-select
              v-model="localForm.area"
              :options="areaOptions"
              required
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Escopo">
            <b-form-select
              v-model="localForm.scope"
              :options="scopeOptionsWithEmpty"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Processo/Escopo detalhado">
            <b-form-input
              v-model.trim="localForm.processContext"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Efetividade (%)">
            <b-form-input
              v-model="localForm.effectiveness"
              type="number"
              min="0"
              max="100"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
    </section>

    <section class="grc-form__section">
      <div class="grc-form__section-header">
        <h3 class="grc-form__section-title">Vínculos e governança</h3>
        <p class="grc-form__section-description">
          Relacione o controle aos projetos, riscos, requisitos e responsáveis.
        </p>
      </div>

      <b-form-group label="Projetos vinculados">
        <SearchableSelect
          v-model="localForm.projects"
          :options="projectOptions"
          multiple
          placeholder="Selecione um ou mais projetos"
          search-placeholder="Buscar projeto..."
          :use-company-filter="false"
          :control-class="'form-control'"
        />
      </b-form-group>

      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Requisitos atendidos">
            <SearchableSelect
              v-model="localForm.requirements"
              :options="requirementOptions"
              multiple
              placeholder="Selecione um ou mais requisitos"
              search-placeholder="Buscar requisito..."
              :use-company-filter="false"
              :control-class="'form-control'"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Riscos mitigados">
            <SearchableSelect
              v-model="localForm.risks"
              :options="riskOptions"
              multiple
              placeholder="Selecione um ou mais riscos"
              search-placeholder="Buscar risco..."
              :use-company-filter="false"
              :control-class="'form-control'"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Executor">
            <b-form-select
              v-model="localForm.executor"
              :options="userOptionsWithEmpty"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Revisor">
            <b-form-select
              v-model="localForm.reviewer"
              :options="userOptionsWithEmpty"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Aprovador de segundo nível">
            <b-form-select
              v-model="localForm.secondLevelApprover"
              :options="userOptionsWithEmpty"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
    </section>

    <section class="grc-form__section">
      <div class="grc-form__section-header">
        <h3 class="grc-form__section-title">Documentação e revisão</h3>
        <p class="grc-form__section-description">
          Registre evidências, achados, histórico e informações de suporte do
          controle.
        </p>
      </div>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Último teste">
            <b-form-input
              v-model="localForm.lastTestAt"
              type="date"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Próximo teste">
            <b-form-input
              v-model="localForm.nextTestAt"
              type="date"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Motivo da atualização">
            <b-form-textarea
              v-model.trim="localForm.lastUpdateReason"
              rows="2"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Documentos de referência">
        <b-form-textarea
          v-model.trim="localForm.referenceDocuments"
          rows="2"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group label="Evidência gerada">
        <b-form-textarea
          v-model.trim="localForm.generatedEvidence"
          rows="2"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group label="Plano de amostragem">
        <b-form-textarea
          v-model.trim="localForm.samplingPlan"
          rows="2"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group label="Achados">
        <b-form-textarea
          v-model.trim="localForm.findings"
          rows="2"
        ></b-form-textarea>
      </b-form-group>

      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Dependências tecnológicas">
            <b-form-textarea
              v-model.trim="localForm.technologyDependencies"
              rows="2"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Segregação de funções">
            <b-form-textarea
              v-model.trim="localForm.segregationOfDuties"
              rows="2"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Histórico de versões">
        <b-form-textarea
          v-model.trim="localForm.versionNotes"
          rows="2"
        ></b-form-textarea>
      </b-form-group>

      <div class="grc-form__toggle-hint">
        Defina se o controle segue ativo para execução, teste e monitoramento.
      </div>
      <b-form-checkbox
        v-model="localForm.active"
        switch
        class="grc-form__toggle"
      >
        Controle ativo
      </b-form-checkbox>
    </section>

    <div class="grc-form__actions">
      <b-button variant="secondary" @click="$emit('cancel')">Cancelar</b-button>
      <b-button
        type="submit"
        variant="primary"
        :disabled="saving || Boolean(submitValidationError)"
      >
        {{ saving ? "Salvando..." : "Salvar" }}
      </b-button>
    </div>
  </b-form>
</template>

<script>
import SearchableSelect from "@/components/SearchableSelect.vue";

const GRCControlStatus = {
  NOT_CONTROLLED: 1,
  PARTIALLY_CONTROLLED: 2,
  CONTROLLED: 3,
  IN_REVIEW: 4,
};

export default {
  name: "GRCControlForm",
  components: {
    SearchableSelect,
  },
  props: {
    controlRecord: {
      type: Object,
      default: null,
    },
    areaOptions: { type: Array, default: () => [] },
    scopeOptions: { type: Array, default: () => [] },
    projectOptions: { type: Array, default: () => [] },
    requirementOptions: { type: Array, default: () => [] },
    riskOptions: { type: Array, default: () => [] },
    userOptions: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      localForm: this.buildForm(this.controlRecord),
      statusValues: GRCControlStatus,
    };
  },
  computed: {
    approvalLabel() {
      if (
        !this.controlRecord ||
        !this.controlRecord.approval ||
        !this.controlRecord.approval.status
      ) {
        return null;
      }
      const labels = {
        "not-required": "Sem aprovação obrigatória",
        pending: "Aguardando aprovação",
        approved: "Aprovado",
        rejected: "Rejeitado",
      };
      return (
        labels[this.controlRecord.approval.status] ||
        this.controlRecord.approval.status
      );
    },
    statusOptions() {
      return [
        this.buildStatusOption(
          this.statusValues.NOT_CONTROLLED,
          "Não controlado"
        ),
        this.buildStatusOption(
          this.statusValues.PARTIALLY_CONTROLLED,
          "Parcialmente controlado"
        ),
        this.buildStatusOption(
          this.statusValues.CONTROLLED,
          "Controlado",
          this.localForm.effectiveness !== "" &&
            this.localForm.effectiveness !== null &&
            this.localForm.effectiveness !== undefined,
          "exige percentual de efetividade"
        ),
        this.buildStatusOption(this.statusValues.IN_REVIEW, "Em revisão"),
      ];
    },
    submitValidationError() {
      if (
        this.localForm.status === this.statusValues.CONTROLLED &&
        (this.localForm.effectiveness === "" ||
          this.localForm.effectiveness === null ||
          this.localForm.effectiveness === undefined)
      ) {
        return "Para salvar como controlado, informe o percentual de efetividade.";
      }

      return "";
    },
    typeOptions() {
      return [
        { value: "preventive", text: "Preventivo" },
        { value: "detective", text: "Detectivo" },
        { value: "corrective", text: "Corretivo" },
      ];
    },
    natureOptions() {
      return [
        { value: "policy", text: "Política" },
        { value: "procedure", text: "Procedimento" },
        { value: "training", text: "Treinamento" },
        { value: "system", text: "Sistema" },
        { value: "manual", text: "Manual" },
        { value: "other", text: "Outro" },
      ];
    },
    criticalityOptions() {
      return [
        { value: "low", text: "Baixa" },
        { value: "medium", text: "Média" },
        { value: "high", text: "Alta" },
        { value: "critical", text: "Crítica" },
      ];
    },
    frequencyOptions() {
      return [
        { value: "event", text: "Por evento" },
        { value: "daily", text: "Diária" },
        { value: "weekly", text: "Semanal" },
        { value: "monthly", text: "Mensal" },
        { value: "quarterly", text: "Trimestral" },
        { value: "annual", text: "Anual" },
      ];
    },
    designTestOptions() {
      return [
        { value: "fit", text: "Apto" },
        { value: "partial", text: "Parcial" },
        { value: "unfit", text: "Não apto" },
      ];
    },
    operationalTestOptions() {
      return [
        { value: "effective", text: "Eficaz" },
        { value: "partial", text: "Parcial" },
        { value: "ineffective", text: "Ineficaz" },
      ];
    },
    scopeOptionsWithEmpty() {
      return [
        { value: null, text: "Selecione um escopo" },
        ...this.scopeOptions,
      ];
    },
    userOptionsWithEmpty() {
      return [
        { value: null, text: "Selecione um usuário" },
        ...this.userOptions,
      ];
    },
  },
  watch: {
    controlRecord: {
      immediate: true,
      handler(value) {
        this.localForm = this.buildForm(value);
      },
    },
  },
  methods: {
    buildStatusOption(value, text, enabled = true, disabledHint = "") {
      return {
        value,
        text,
        disabled: !enabled && this.localForm.status !== value,
        ...(disabledHint && !enabled && this.localForm.status !== value
          ? { text: `${text} (${disabledHint})` }
          : {}),
      };
    },
    buildForm(controlRecord) {
      return {
        name: controlRecord?.name || "",
        objective: controlRecord?.objective || "",
        controlType: controlRecord?.controlType || "preventive",
        nature: controlRecord?.nature || "policy",
        processContext: controlRecord?.processContext || "",
        internalCategory: controlRecord?.internalCategory || "",
        area: controlRecord?.area?._id || controlRecord?.area || null,
        scope: controlRecord?.scope?._id || controlRecord?.scope || null,
        projects: Array.isArray(controlRecord?.projects)
          ? controlRecord.projects.map((item) => item._id || item)
          : [],
        requirements: Array.isArray(controlRecord?.requirements)
          ? controlRecord.requirements.map((item) => item._id || item)
          : [],
        risks: Array.isArray(controlRecord?.risks)
          ? controlRecord.risks.map((item) => item._id || item)
          : [],
        frequency: controlRecord?.frequency || "monthly",
        executor:
          controlRecord?.executor?._id || controlRecord?.executor || null,
        reviewer:
          controlRecord?.reviewer?._id || controlRecord?.reviewer || null,
        secondLevelApprover:
          controlRecord?.secondLevelApprover?._id ||
          controlRecord?.secondLevelApprover ||
          null,
        criticality: controlRecord?.criticality || "medium",
        referenceDocuments: controlRecord?.referenceDocuments || "",
        generatedEvidence: controlRecord?.generatedEvidence || "",
        designTest: controlRecord?.designTest || "partial",
        operationalTest: controlRecord?.operationalTest || "partial",
        samplingPlan: controlRecord?.samplingPlan || "",
        lastTestAt: this.formatDateForInput(controlRecord?.lastTestAt),
        nextTestAt: this.formatDateForInput(controlRecord?.nextTestAt),
        effectiveness:
          controlRecord?.effectiveness === 0 || controlRecord?.effectiveness
            ? controlRecord.effectiveness
            : "",
        findings: controlRecord?.findings || "",
        technologyDependencies: controlRecord?.technologyDependencies || "",
        segregationOfDuties: controlRecord?.segregationOfDuties || "",
        versionNotes: controlRecord?.versionNotes || "",
        lastUpdateReason: controlRecord?.lastUpdateReason || "",
        status: controlRecord?.status || GRCControlStatus.NOT_CONTROLLED,
        active: controlRecord?.active !== false,
      };
    },
    formatDateForInput(value) {
      if (!value) {
        return null;
      }
      return new Date(value).toISOString().slice(0, 10);
    },
    normalizeId(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }
      const parsed = parseInt(value);
      return Number.isNaN(parsed) ? null : parsed;
    },
    submitForm() {
      if (this.submitValidationError) {
        return;
      }

      this.$emit("submit", {
        ...this.localForm,
        area: this.normalizeId(this.localForm.area),
        scope: this.normalizeId(this.localForm.scope),
        projects: (this.localForm.projects || [])
          .map((item) => this.normalizeId(item))
          .filter(Boolean),
        requirements: (this.localForm.requirements || [])
          .map((item) => this.normalizeId(item))
          .filter(Boolean),
        risks: (this.localForm.risks || [])
          .map((item) => this.normalizeId(item))
          .filter(Boolean),
        executor: this.normalizeId(this.localForm.executor),
        reviewer: this.normalizeId(this.localForm.reviewer),
        secondLevelApprover: this.normalizeId(
          this.localForm.secondLevelApprover
        ),
        effectiveness:
          this.localForm.effectiveness === ""
            ? null
            : parseInt(this.localForm.effectiveness),
        status: parseInt(this.localForm.status),
        lastTestAt: this.localForm.lastTestAt || null,
        nextTestAt: this.localForm.nextTestAt || null,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@use "./grc-form.scss";
</style>
